// third party imports
import { useState, useEffect, useMemo, useCallback, useContext } from "react";

// internal rebet imports
import "./stylesKPIs.css";
import {
  SIDER_TABS,
  ADMIN_TYPES,
  KPI_PAGE_PERMISSIONS,
  SHOW_ALL_KPI_SECTIONS,
} from "../../utils/Constants";
import { TRIVELTA_PRODUCTS } from "../../utils/products";
import { AppStrings } from "../../utils/constants/AppStrings";
import FeatureFlagService from "../../services/FeatureFlagService";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";

// component imports
import OverviewV2 from "./OverviewV2/OverviewV2";
import FinancialsV2 from "./FinancialsV2/FinancialsV2";
import Acquisition from "./Acquisition/Acquisition";
import SportsbookKPIsV2 from "./SportsbookKpi2/SportsbookKPIsV2";
import CasinoKPIs from "./CasinoKPIs/CasinoKPIs";
import AppContext from "../../context/AppContext";
import {
  KpiTabToolbarProvider,
  useKpiTabToolbarSlot,
} from "./KpiTabToolbarContext";
import { usePageTitle } from "../CommonComponents/Layout/PageTitleContext";

const KPIsLayout = () => {
  usePageTitle(AppStrings.KPIS);

  const tabToolbar = useKpiTabToolbarSlot();
  const currencySymbol = useCurrencySymbol();
  const { setActiveSidebarTab, adminType, adminPermissions, adminDataLoaded } =
    useContext(AppContext);

  // Overview is hardcoded to superadmin and is never configurable per role.
  const isSuperAdmin =
    adminType === ADMIN_TYPES.SUPER_ADMIN ||
    adminType === ADMIN_TYPES.NEW_SUPER_ADMIN;

  // True when the role has the page-level KPI permission granted.
  // While SHOW_ALL_KPI_SECTIONS is on, every permission-gated tab is forced visible.
  const hasKpiPage = useCallback(
    (permissionKey) =>
      SHOW_ALL_KPI_SECTIONS ||
      (Array.isArray(adminPermissions) &&
        adminPermissions.includes(permissionKey)),
    [adminPermissions],
  );

  // Defer tab content until admin permissions are loaded.
  const [activeTab, setActiveTab] = useState(null);
  const [hasSportsbook, setHasSportsbook] = useState(false);
  const [hasCasino, setHasCasino] = useState(false);

  // TODO(PAMENG-1820): drop the "sportsbook_v2"/"casino" fallback args after rollout.
  useEffect(() => {
    const resolveEnabledTabs = async () => {
      await FeatureFlagService.initialize();
      setHasSportsbook(
        FeatureFlagService.isProductActive(
          TRIVELTA_PRODUCTS.SPORTSBOOK,
          "sportsbook_v2",
        ),
      );
      setHasCasino(
        FeatureFlagService.isProductActive(TRIVELTA_PRODUCTS.CASINO, "casino"),
      );
    };
    resolveEnabledTabs();
  }, []);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.KPIS);
  }, [setActiveSidebarTab]);

  // Build tabs dynamically based on the role's page-level KPI permissions.
  // Overview is superadmin-only; the rest are toggled per role in
  // Staff & Permissions -> Roles.
  const tabs = useMemo(() => {
    const visibleTabs = [];

    if (isSuperAdmin) {
      visibleTabs.push({
        label: AppStrings.OVERVIEW,
        value: "overview-v2",
      });
    }

    if (hasKpiPage(KPI_PAGE_PERMISSIONS.FINANCIALS)) {
      visibleTabs.push({
        label: AppStrings.FINANCIALS,
        value: "financials-v2",
      });
    }

    // Order: Overview -> Financials -> Acquisition -> Sportsbook -> Casino.
    // Acquisition and Casino are V2-only sections (not present in legacy KPIs).
    if (hasKpiPage(KPI_PAGE_PERMISSIONS.ACQUISITION)) {
      visibleTabs.push({
        label: AppStrings.ACQUISITION,
        value: "acquisition",
      });
    }

    // Sportsbook also depends on the `sportsbook_v2` feature flag.
    // Sits between Acquisition and Casino so the tab strip mirrors the
    // funnel order admins reason about (acquisition -> sportsbook bets ->
    // casino activity).
    if (hasSportsbook && hasKpiPage(KPI_PAGE_PERMISSIONS.SPORTSBOOK)) {
      visibleTabs.push({
        label: AppStrings.SPORTSBOOK,
        value: "sportsbook-v2",
      });
    }

    // Casino, like Sportsbook, requires the product active in addition to the
    // permission — parity guard so a regular role stops seeing the section when
    // the casino product is off (until the server-side /auth/me gate∩permission
    // intersection makes this dynamic for all roles; see the facade followups).
    if (hasCasino && hasKpiPage(KPI_PAGE_PERMISSIONS.CASINO)) {
      visibleTabs.push({
        label: AppStrings.CASINO,
        value: "casino",
      });
    }

    return visibleTabs;
  }, [hasSportsbook, hasCasino, isSuperAdmin, hasKpiPage]);

  // Pick the first visible tab once permissions (and tabs) are known.
  useEffect(() => {
    if (!adminDataLoaded || !tabs.length) return;
    if (activeTab == null || !tabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(tabs[0].value);
    }
  }, [adminDataLoaded, tabs, activeTab]);

  const isActiveTabVisible =
    adminDataLoaded &&
    activeTab != null &&
    tabs.some((tab) => tab.value === activeTab);

  return (
    <>
      <div className="kpis-container">
        {/* Tabs + per-tab controls (date picker, export) on one row */}
        <div className="kpis-tabs-row">
          <div className="kpis-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                className={tab.value === activeTab ? "active" : ""}
                onClick={() => setActiveTab(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="kpis-tab-toolbar">{tabToolbar}</div>
        </div>

        {/* Content — only render when the active tab is one the role can see */}
        {isActiveTabVisible && (
          <div className="kpis-content">
            {activeTab === "financials-v2" && (
              <FinancialsV2 currencySymbol={currencySymbol} />
            )}
            {activeTab === "overview-v2" && (
              <OverviewV2
                onNavigateToTab={setActiveTab}
                currencySymbol={currencySymbol}
              />
            )}
            {activeTab === "acquisition" && (
              <Acquisition currencySymbol={currencySymbol} />
            )}
            {activeTab === "sportsbook-v2" && (
              <SportsbookKPIsV2 currencySymbol={currencySymbol} />
            )}
            {activeTab === "casino" && (
              <CasinoKPIs currencySymbol={currencySymbol} />
            )}
          </div>
        )}
      </div>
    </>
  );
};

const KPIs = () => (
  <KpiTabToolbarProvider>
    <KPIsLayout />
  </KpiTabToolbarProvider>
);

export default KPIs;
